/** One-search lazy estimates: deduplicated, abortable, and never reused across contexts. */
export class PrototypeMapPrices {
    constructor(fetcher = (...args) => fetch(...args)) {
        this.fetcher = fetcher;
        this.context = null;
        this.prices = {};
        this.pending = new Map();
        this.expired = false;
    }

    reset() {
        for (const { controller } of this.pending.values()) controller.abort();
        this.pending.clear();
        this.context = null;
        this.prices = {};
        this.expired = false;
    }

    setContext(context, prices = {}) {
        if (context !== this.context) this.reset();
        this.context = context;
        Object.assign(this.prices, prices);
    }

    async get(listingId) {
        const id = String(listingId);
        const context = this.context;
        if (!context) return { state: 'stale' };
        if (this.expired) return { state: 'expired' };
        if (Object.hasOwn(this.prices, id)) {
            return { state: 'ready', price: this.prices[id] };
        }
        if (this.pending.has(id)) return this.pending.get(id).promise;
        const controller = new AbortController();
        const promise = (async () => {
            try {
                const params = new URLSearchParams({
                    pricingContext: context,
                    listingId: id
                });
                const response = await this.fetcher(
                    `/guesty/search/prototype-map-price?${params}`,
                    {
                        signal: controller.signal,
                        cache: 'no-store'
                    }
                );
                const data = await response.json();
                if (controller.signal.aborted || context !== this.context)
                    return { state: 'stale' };
                if (
                    response.status === 409 &&
                    data.code === 'SEARCH_CONTEXT_EXPIRED'
                ) {
                    this.expired = true;
                    return { state: 'expired' };
                }
                if (
                    !response.ok ||
                    !data.success ||
                    !Number.isFinite(data.price)
                ) {
                    return { state: 'failed' };
                }
                this.prices[id] = data.price;
                return { state: 'ready', price: data.price };
            } catch {
                return {
                    state:
                        controller.signal.aborted || context !== this.context
                            ? 'stale'
                            : 'failed'
                };
            } finally {
                if (this.pending.get(id)?.controller === controller)
                    this.pending.delete(id);
            }
        })();
        this.pending.set(id, { controller, promise });
        return promise;
    }
}
